import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import FuzzyProjectSelect from './FuzzyProjectSelect';

const API_URL = process.env.REACT_APP_API_URL || '';

const getToken = () => localStorage.getItem('notes_token') || '';

const fetchWithAuth = async (url, options = {}) => {
  const token = getToken();
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'x-token': token
    }
  });
};

export default function MonitorDataViewer() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(projectId || '');
  
  const [filterDate, setFilterDate] = useState('');
  const [filterTimePoint, setFilterTimePoint] = useState('');
  const [saving, setSaving] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [editOnline, setEditOnline] = useState('');
  const [editResponse, setEditResponse] = useState('');

  // 加载项目列表
  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) loadRecords();
  }, [selectedProject, filterDate, filterTimePoint]);

  const loadProjects = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/monitor/projects`);
      const data = await res.json();
      if (data.success) {
        setProjects(data.projects || []);
      }
    } catch (e) { console.error(e); }
  };

  const loadRecords = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/monitor/records/${selectedProject}`;
      const params = [];
      if (filterDate) params.push(`date=${filterDate}`);
      if (filterTimePoint) params.push(`timePoint=${filterTimePoint}`);
      if (params.length > 0) url += '?' + params.join('&');
      
      const res = await fetchWithAuth(url);
      const data = await res.json();
      if (data.success) {
        setRecords(data.records || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const openEdit = (rec) => {
    setEditRecord(rec);
    setEditOnline(rec.online.toString());
    setEditResponse(rec.responseTime.toString());
  };

  const saveEdit = async () => {
    if (!editRecord) return;
    setSaving(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/api/monitor/record/update/${selectedProject}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            date: editRecord.date,
            timePoint: editRecord.timePoint,
            scenarioId: editRecord.scenarioId,
            online: parseInt(editOnline) || 0,
            responseTime: parseFloat(editResponse) || 0
          })
        }
      );
      const data = await res.json();
      if (data.success) {
        alert('保存成功');
        setEditRecord(null);
        loadRecords();
      }
    } catch (e) { alert('保存失败: ' + e.message); }
    setSaving(false);
  };

  // 获取唯一时点列表
  const timePoints = [...new Set(records.map(r => r.timePoint))].sort();

  return (
    <div className="monitor-data-viewer">
      <div className="page-header">
        <button onClick={() => navigate('/monitor')} className="btn-back">← 返回看板</button>
        <h1>📋 监控数据查看/编辑</h1>
      </div>

      {/* 筛选栏 */}
      <div className="filter-bar">
        <div className="filter-group">
          <label>项目</label>
          <FuzzyProjectSelect
            projects={projects}
            value={selectedProject}
            onChange={setSelectedProject}
            placeholder="请选择项目"
          />
        </div>
        <div className="filter-group">
          <label>日期</label>
          <input
            type="date"
            value={filterDate}
            onChange={e => setFilterDate(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>时点</label>
          <select value={filterTimePoint} onChange={e => setFilterTimePoint(e.target.value)}>
            <option value="">全部</option>
            {timePoints.map(tp => (
              <option key={tp} value={tp}>{tp}</option>
            ))}
          </select>
        </div>
        <button onClick={loadRecords} className="btn-primary">查询</button>
      </div>

      {loading && <div className="loading">加载中...</div>}

      {/* 数据表格 */}
      {!loading && (
        <div className="data-table-wrap">
          <p className="record-count">共 {records.length} 条记录（已排除responseTime=0的无效数据）</p>
          <table className="data-table">
            <thead>
              <tr>
                <th>日期</th>
                <th>时点</th>
                <th>场景</th>
                <th>在线人数</th>
                <th>响应时间(s)</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {records.map((rec, idx) => (
                <tr key={idx}>
                  <td>{rec.date}</td>
                  <td>{rec.timePoint}</td>
                  <td>{rec.scenarioName || rec.scenarioId}</td>
                  <td>{rec.online}</td>
                  <td>{rec.responseTime}</td>
                  <td>
                    <button
                      onClick={() => openEdit(rec)}
                      className="btn-secondary"
                      style={{ fontSize: '12px', padding: '4px 10px' }}
                    >编辑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 编辑弹窗 */}
      {editRecord && (
        <div className="modal-overlay" onClick={() => setEditRecord(null)}>
          <div className="modal-content edit-modal-box" onClick={e => e.stopPropagation()}>
            <div className="edit-modal-header">
              <h3>编辑记录</h3>
              <button className="modal-close" onClick={() => setEditRecord(null)}>×</button>
            </div>
            <div className="edit-modal-info">
              <span>📅 {editRecord.date}</span>
              <span>🕐 {editRecord.timePoint}</span>
              <span>📊 {editRecord.scenarioName || editRecord.scenarioId}</span>
            </div>
            <div className="edit-modal-body">
              <div className="form-group">
                <label>在线人数</label>
                <input
                  type="number"
                  value={editOnline}
                  onChange={e => setEditOnline(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>响应时间(s)</label>
                <input
                  type="number"
                  step="0.1"
                  value={editResponse}
                  onChange={e => setEditResponse(e.target.value)}
                />
              </div>
            </div>
            <div className="edit-modal-footer">
              <button onClick={saveEdit} className="btn-primary" disabled={saving}>
                {saving ? '保存中...' : '保存'}
              </button>
              <button onClick={() => setEditRecord(null)} className="btn-secondary">取消</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
